import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAuth } from '../contexts/AuthContext';

interface ActivityData {
  date: string;
  registrations: number;
  activeUsers: number;
}

interface AdminStats {
  totalUsers: number;
  totalPortfolioItems: number;
  newUsersLast7Days: number;
  newUsersThisMonth: number;
  averageSessionSeconds: number;
}

interface AdminPanelData {
  users: UserStats[];
  stats: AdminStats;
}

interface UserStats {
  id: number;
  email: string;
  name: string;
  created_at?: string;
  createdAt?: string;
  lastLogin?: string;
  registrationDate?: string;
  registrationTime?: string;
  usageDays?: number;
  usageHours?: number;
  usageText?: string;
  totalSessionSeconds?: number;
  total_seconds?: number;
}

const AdminPanel: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<UserStats[]>([]);
  const [activity, setActivity] = useState<ActivityData[]>([]);
  const [showActivityChart, setShowActivityChart] = useState(false);
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    totalPortfolioItems: 0,
    newUsersLast7Days: 0,
    newUsersThisMonth: 0,
    averageSessionSeconds: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  // Toplam oturum süresini okunabilir formata çevir
  const formatSessionDuration = (totalSeconds?: number) => {
    if (!totalSeconds || totalSeconds <= 0) return '-';
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) return `${hours} sa ${minutes} dk`;
    if (minutes > 0) return `${minutes} dk ${seconds} sn`;
    return `${seconds} sn`;
  };

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      setError('Admin yetkisi gerekli. Lütfen admin olarak giriş yapın.');
      return;
    }
    fetchSessionStats();
  }, [isAdmin]);

  // Backend'den oturum sürelerini ve aktivite verilerini çek
  const fetchSessionStats = async () => {
    try {
      setLoading(true);
      let token = localStorage.getItem('token');
      if (token && token.startsWith('Bearer ')) {
        token = token.substring(7);
      }

      // Her iki endpoint'i parallel olarak çağır
      const [sessionResponse, activityResponse] = await Promise.all([
        fetch('/api/user-session', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }),
        fetch('/api/activity', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }),
      ]);

      if (!sessionResponse.ok) {
        if (sessionResponse.status === 401) {
          throw new Error('Admin yetkisi gerekli. Lütfen admin olarak giriş yapın.');
        } else if (sessionResponse.status === 404) {
          throw new Error('Oturum verisi hizmeti kullanılamıyor.');
        }
        throw new Error(`Hata (${sessionResponse.status}): Oturum verileri alınamadı`);
      }

      const sessionData = await sessionResponse.json() as AdminPanelData;
      setUsers(sessionData.users);
      setStats(sessionData.stats);

      if (activityResponse.ok) {
        const activityData = await activityResponse.json() as ActivityData[];
        setActivity(activityData);
      }

      setError('');
    } catch (err: any) {
      setError('Verileri yüklenirken hata oluştu: ' + err.message);
      console.error('Admin panel error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-panel">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-panel">
        <div className="error-message">
          <h3>❌ Erişim Hatası</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h2>👨‍💼 Yönetici Paneli</h2>
        <p>Sisteme kayıtlı tüm kullanıcıların istatistikleri</p>
      </div>

      <div className="admin-stats">
        <div className="stat-card">
          <h3>👥 Toplam Kullanıcı</h3>
          <p className="stat-value">{stats.totalUsers}</p>
        </div>
        <div className="stat-card">
          <h3>📊 Portfolio Items</h3>
          <p className="stat-value">{stats.totalPortfolioItems}</p>
        </div>
        <div className="stat-card">
          <h3>📈 Son 7 Gün Yeni</h3>
          <p className="stat-value">{stats.newUsersLast7Days}</p>
        </div>
        <div className="stat-card">
          <h3>📅 Bu Ay Yeni</h3>
          <p className="stat-value">{stats.newUsersThisMonth}</p>
        </div>
        <div className="stat-card">
          <h3>⏰ Ort. Online Süresi</h3>
          <p className="stat-value">{formatSessionDuration(stats.averageSessionSeconds)}</p>
        </div>
      </div>

      <div className="activity-chart-container">
        <h3 
          style={{ cursor: 'pointer' }}
          onClick={() => setShowActivityChart(!showActivityChart)}
        >
          📈 Son 30 Gün Aktivite {showActivityChart ? '▼' : '▶'}
        </h3>
        {showActivityChart && activity && activity.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={activity} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
              <XAxis 
                dataKey="date" 
                stroke="var(--text-secondary)"
                tick={{ fontSize: 12 }}
              />
              <YAxis stroke="var(--text-secondary)" tick={{ fontSize: 12 }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'var(--card)', 
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)'
                }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="registrations" 
                stroke="var(--primary)" 
                name="Yeni Kayıtlar"
                strokeWidth={2}
                dot={{ fill: 'var(--primary)', r: 3 }}
              />
              <Line 
                type="monotone" 
                dataKey="activeUsers" 
                stroke="var(--success)" 
                name="Aktif Kullanıcılar"
                strokeWidth={2}
                dot={{ fill: 'var(--success)', r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>Aktivite verisi yükleniyor...</p>
        )}
      </div>

      <div className="users-table-container">
        <h3>📋 Kullanıcı Listesi</h3>
        <div className="users-table">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Ad Soyad</th>
                <th>E-posta</th>
                <th>Kayıt Tarihi</th>
                <th>Kayıt Saati</th>
                <th>Toplam Online Süresi</th>
              </tr>
            </thead>
            <tbody>
              {users && users.map ? (
                (() => {
                  console.log(`📋 Rendering ${users.length} table rows`);
                  return users.map((user) => {
                    const registrationDate = user.created_at ? new Date(user.created_at).toLocaleDateString('tr-TR') : '-';
                    const registrationTime = user.created_at ? new Date(user.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '-';
                    return (
                      <tr key={user.id}>
                        <td className="user-id">#{user.id}</td>
                        <td className="user-name">{user.name}</td>
                        <td className="user-email">{user.email}</td>
                        <td className="user-registration-date">{registrationDate}</td>
                        <td className="user-registration-time">{registrationTime}</td>
                        <td className="user-session-duration">
                          <span className="usage-badge">
                            {formatSessionDuration(user.total_seconds || user.totalSessionSeconds)}
                          </span>
                        </td>
                      </tr>
                    );
                  });
                })()
              ) : (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '20px' }}>
                    Kullanıcı verisi bulunamadı
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
